import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { StatisticsChartCardToolbar, StatisticsChartCardFullscreenAction } from '../../common/statistic-chart-card/StatisticsChartCardActions';
import OrderPrimaryPhaseDimensionChart from './OrderPrimaryPhaseDimensionChart';
import OrderPrimaryPhaseDimensionSwitch from './OrderPrimaryPhaseDimensionSwitch';
import { getOrderPrimaryPhaseDimensionLabel } from './lib/orderPrimaryPhaseDimension';
import { getPrimaryPhaseMetricLabel, getPrimaryPhaseRowMetricValue, ORDER_PRIMARY_PHASE_METRICS } from './lib/orderPrimaryPhaseMetric';

const OrderPrimaryPhaseDimensionPanel = ({
	rows,
	isLoading,
	activeDimension,
	onDimensionChange,
	activeMetric,
	selectedKey,
	onSelectRow,
	animationKey,
	barHeight = '14rem',
	topN = 12,
	className = '',
	showHint = true,
	fillSlot = false,
	plotBodyStyle = null,
	onFullscreen,
	enableOrderDrill = false,
	onDrillSelect,
}) => {
	const dimensionLabel = getOrderPrimaryPhaseDimensionLabel(activeDimension);
	const metricLabel = getPrimaryPhaseMetricLabel(activeMetric);
	const [localSelectedKey, setLocalSelectedKey] = useState(null);
	const rowSelectionControlled = typeof onSelectRow === 'function';
	const effectiveSelectedKey = rowSelectionControlled ? selectedKey : localSelectedKey;

	useEffect(() => {
		setLocalSelectedKey(null);
	}, [activeDimension, animationKey]);

	const summarySalesTotal = useMemo(
		() => {
			if (effectiveSelectedKey) {
				const row = (rows ?? []).find((item) => item.key === effectiveSelectedKey);
				return row ? getPrimaryPhaseRowMetricValue(row, ORDER_PRIMARY_PHASE_METRICS.TOTAL_PRICE) : 0;
			}
			return (rows ?? []).reduce((sum, row) => sum + getPrimaryPhaseRowMetricValue(row, ORDER_PRIMARY_PHASE_METRICS.TOTAL_PRICE), 0);
		},
		[effectiveSelectedKey, rows],
	);
	const summaryOrderCount = useMemo(
		() => {
			if (effectiveSelectedKey) {
				const row = (rows ?? []).find((item) => item.key === effectiveSelectedKey);
				return row ? getPrimaryPhaseRowMetricValue(row, ORDER_PRIMARY_PHASE_METRICS.COUNT) : 0;
			}
			return (rows ?? []).reduce((sum, row) => sum + getPrimaryPhaseRowMetricValue(row, ORDER_PRIMARY_PHASE_METRICS.COUNT), 0);
		},
		[effectiveSelectedKey, rows],
	);

	const handleSelectRow = useCallback(
		(key) => {
			if (enableOrderDrill && onDrillSelect) {
				const row = rows?.find((item) => item.key === key);
				onDrillSelect({
					kind: 'dimension',
					dimension: activeDimension,
					dimensionKey: key,
					label: row?.label ?? key,
				});
				return;
			}
			if (rowSelectionControlled) {
				onSelectRow(key);
				return;
			}
			setLocalSelectedKey((prev) => (prev === key ? null : key ?? null));
		},
		[activeDimension, enableOrderDrill, onDrillSelect, onSelectRow, rowSelectionControlled, rows],
	);

	return (
		<div className={['statistics-dimension-panel', fillSlot ? 'statistics-dimension-panel--fill-slot' : null, className].filter(Boolean).join(' ')}>
			<div className='statistics-dimension-panel__head'>
				<div className='statistics-dimension-panel__head-main'>
					{fillSlot ? null : (
						<>
							<h3 className='statistics-dimension-panel__title mb-0'>{dimensionLabel}贡献排行</h3>
							{showHint ? (
								<p className='statistics-dimension-panel__hint mb-0'>
									按{metricLabel}排序 · 点击条形图筛选{dimensionLabel}订单
								</p>
							) : null}
						</>
					)}
				</div>
				<StatisticsChartCardToolbar>
					<OrderPrimaryPhaseDimensionSwitch activeDimension={activeDimension} onDimensionChange={onDimensionChange} compact />
					{onFullscreen ? <StatisticsChartCardFullscreenAction onClick={onFullscreen} title='全屏查看订单明细' /> : null}
				</StatisticsChartCardToolbar>
			</div>
			<div
				className={[
					'statistics-dimension-panel__plot',
					fillSlot ? 'statistics-pipeline-preset-chart statistics-pipeline-preset-chart--fill' : null,
					plotBodyStyle ? 'statistics-pipeline-page__chart-plot-body' : null,
				]
					.filter(Boolean)
					.join(' ')}
				style={plotBodyStyle ?? undefined}
			>
				<OrderPrimaryPhaseDimensionChart
					rows={rows}
					isLoading={isLoading}
					activeMetric={activeMetric}
					selectedKey={effectiveSelectedKey}
					onSelectRow={handleSelectRow}
					animationKey={animationKey}
					barHeight={barHeight}
					topN={topN}
					fillSlot={fillSlot}
					summarySalesTotal={summarySalesTotal}
					summaryOrderCount={summaryOrderCount}
				/>
			</div>
		</div>
	);
};

OrderPrimaryPhaseDimensionPanel.propTypes = {
	rows: PropTypes.array,
	isLoading: PropTypes.bool,
	activeDimension: PropTypes.string.isRequired,
	onDimensionChange: PropTypes.func.isRequired,
	activeMetric: PropTypes.string,
	selectedKey: PropTypes.string,
	onSelectRow: PropTypes.func,
	animationKey: PropTypes.string,
	barHeight: PropTypes.string,
	topN: PropTypes.number,
	className: PropTypes.string,
	showHint: PropTypes.bool,
	fillSlot: PropTypes.bool,
	plotBodyStyle: PropTypes.object,
	onFullscreen: PropTypes.func,
	enableOrderDrill: PropTypes.bool,
	onDrillSelect: PropTypes.func,
};

export default memo(OrderPrimaryPhaseDimensionPanel);
